import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  TableContainer,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/common/PageLayout';
import KotilingaInventorySlotsManager from '../components/kotilinga/KotilingaInventorySlotsManager';
import { get } from '../utils/api';

export default function KotilingaInventoryByCategoryPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState('');
  const [rows, setRows] = React.useState([]);

  const loadCounts = React.useCallback(async () => {
    try {
      const res = await get('/ldb/kotilinga-inventory/counts-by-category');
      setRows(Array.isArray(res?.data) ? res.data : []);
      setErr('');
    } catch (e) {
      setErr(e?.message || 'Failed to load category counts');
    }
  }, []);

  React.useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      await loadCounts();
      if (!cancel) setLoading(false);
    })();
    return () => { cancel = true; };
  }, [loadCounts]);

  const totals = React.useMemo(() => {
    let inventory_count = 0;
    let taken = 0;
    let blocked = 0;
    let available = 0;
    let other = 0;
    for (const r of rows) {
      inventory_count += Number(r.inventory_count) || 0;
      taken += Number(r.taken) || 0;
      blocked += Number(r.blocked) || 0;
      available += Number(r.available) || 0;
      other += Number(r.other) || 0;
    }
    return { inventory_count, taken, blocked, available, other };
  }, [rows]);

  const showOther = totals.other > 0;

  return (
    <PageLayout contentMaxWidth="md" showHeaderImage={false} showOrgHeader>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate(-1)} size="small" aria-label="Back">
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 280 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Inventory by category</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Summary counts below; scroll down to map WooCommerce products and manage sponsors/customers per slot.
          </Typography>
        </Box>
      </Box>

      {err && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Inventory</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Taken</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Blocked</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Available</TableCell>
                  {showOther ? (
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Other</TableCell>
                  ) : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={showOther ? 6 : 5}>
                      <Typography variant="body2" color="text.secondary">No inventory rows</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {rows.map((r) => (
                      <TableRow key={String(r.category_id ?? 'null')} hover>
                        <TableCell>{r.category_name || '—'}</TableCell>
                        <TableCell align="right">{Number(r.inventory_count || 0).toLocaleString()}</TableCell>
                        <TableCell align="right">{Number(r.taken || 0).toLocaleString()}</TableCell>
                        <TableCell align="right">{Number(r.blocked || 0).toLocaleString()}</TableCell>
                        <TableCell align="right">{Number(r.available || 0).toLocaleString()}</TableCell>
                        {showOther ? (
                          <TableCell align="right">{Number(r.other || 0).toLocaleString()}</TableCell>
                        ) : null}
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{totals.inventory_count.toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{totals.taken.toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{totals.blocked.toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{totals.available.toLocaleString()}</TableCell>
                      {showOther ? (
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{totals.other.toLocaleString()}</TableCell>
                      ) : null}
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Divider sx={{ my: 4 }} />

      <KotilingaInventorySlotsManager onInventoryMutated={loadCounts} />
    </PageLayout>
  );
}
